'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { Cross } from '@/components/site/Cross'
import { track } from '@/lib/analytics'
import {
    ACCEPT_ALL,
    CONSENT_EVENT,
    NECESSARY_ONLY,
    applyConsent,
    readConsent,
    storeConsent,
    type Consent,
} from '@/lib/consent'

/**
 * The question a Dutch site has to ask before it measures anything.
 *
 * It renders nothing until the browser has been read, which is deliberate:
 * this is the only part of the page whose content depends on `localStorage`,
 * and rendering it on the server would either show the banner to someone who
 * already answered or hydrate into a mismatch.
 *
 * Refusing is one click, in the same weight as accepting. A banner where "no"
 * is three clicks and grey is not consent, whatever it collects.
 *
 * It stands in the bottom-left corner as a panel rather than across the foot of
 * the page: a full-width bar covers the thing a visitor came to read, and it is
 * the one element on the site that would announce itself by ignoring the
 * drawing everything else follows. So it takes the hairline border and the
 * crosses the rails use, and leaves the page visible beside it.
 */
export function CookieBanner() {
    const panel = useRef<HTMLDivElement>(null)
    const [open, setOpen] = useState(false)
    const [settings, setSettings] = useState(false)
    const [choice, setChoice] = useState<Consent>(NECESSARY_ONLY)

    useEffect(() => {
        const stored = readConsent()

        if (stored === null) {
            setOpen(true)

            return
        }

        setChoice(stored)
    }, [])

    // The footer's "Cookievoorkeuren" reaches the banner through the document
    // rather than through a store: one listener beats threading a setter
    // through a server-rendered footer.
    useEffect(() => {
        function reopen() {
            setChoice(readConsent() ?? NECESSARY_ONLY)
            setSettings(true)
            setOpen(true)

            // Asked for, so it takes focus. The banner that opens by itself on
            // a first visit does not: moving someone out of the page they came
            // to read, to a question they did not ask, is what makes these
            // things hated.
            requestAnimationFrame(() => panel.current?.focus())
        }

        document.addEventListener(CONSENT_EVENT, reopen)

        return () => document.removeEventListener(CONSENT_EVENT, reopen)
    }, [])

    useEffect(() => {
        if (!open) {
            return
        }

        function onKey(event: KeyboardEvent) {
            // Dismissing without answering would leave the page measuring
            // nothing while looking as though it had been allowed to. There is
            // no way out of the question except answering it — unless it was
            // reopened from the footer, in which case the stored answer stands.
            if (event.key === 'Escape' && readConsent() !== null) {
                setOpen(false)
            }
        }

        document.addEventListener('keydown', onKey)

        return () => document.removeEventListener('keydown', onKey)
    }, [open])

    function decide(consent: Consent, action: 'accept_all' | 'necessary_only' | 'save_choice') {
        storeConsent(consent)
        applyConsent(consent)
        // After the update, so the container knows what it may do with it.
        track({
            event: 'consent_update',
            consent_action: action,
            consent_analytics: consent.analytics,
            consent_marketing: consent.marketing,
        })
        setChoice(consent)
        setSettings(false)
        setOpen(false)
    }

    if (!open) {
        return null
    }

    return (
        <div
            ref={panel}
            role="dialog"
            aria-label="Cookievoorkeuren"
            tabIndex={-1}
            // A panel in the corner of the window on a screen with room for
            // one. On a phone there is no corner to sit in — it is nearly the
            // full width, and its edges then run beside the page's own — so
            // there it takes the rail the content band draws rather than an
            // offset of its own.
            className="fixed bottom-6 left-6 z-100 w-[min(424px,calc(100vw-3rem))] border border-hair bg-bg max-sm:right-rail max-sm:bottom-4 max-sm:left-rail max-sm:w-auto"
        >
            {/* The corners, where this panel's rules meet — the same plus the
             * rails draw where a section boundary crosses them. */}
            <Cross className="top-0 left-0" />
            <Cross className="top-0 left-full" />
            <Cross className="top-full left-0" />
            <Cross className="top-full left-full" />

            <div className="flex items-center justify-between gap-4 border-b border-hair px-5 py-3">
                <span className="font-mono text-[10.5px] tracking-[0.08em] text-faint uppercase">
                    Cookies
                </span>
                <span className="font-mono text-[10.5px] text-faint">
                    {settings ? 'Instellingen' : 'Toestemming'}
                </span>
            </div>

            <div className="flex flex-col gap-3 px-5 py-[18px]">
                <p className="m-0 text-[14px] leading-relaxed text-pretty text-dim">
                    Deze site werkt zonder cookies. Alleen als je het goedvindt meet ik hoe de site gebruikt
                    wordt, zodat ik weet wat werkt.{' '}
                    <Link href="/privacy" className="text-fg underline underline-offset-4">
                        Lees het privacybeleid
                    </Link>
                    .
                </p>

                {settings ? (
                    <div className="flex flex-col gap-2 border-t border-hair pt-3">
                        <Toggle
                            label="Statistieken"
                            hint="Hoeveel bezoekers er zijn en welke pagina's ze lezen."
                            checked={choice.analytics}
                            onChange={(analytics) => setChoice({ ...choice, analytics })}
                        />
                        <Toggle
                            label="Marketing"
                            hint="Advertenties meten. Staat uit tenzij je het aanzet."
                            checked={choice.marketing}
                            onChange={(marketing) => setChoice({ ...choice, marketing })}
                        />
                    </div>
                ) : null}
            </div>

            <div className="flex items-stretch border-t border-hair">
                {settings ? (
                    <button
                        type="button"
                        onClick={() => decide(choice, 'save_choice')}
                        className="flex-none cursor-pointer border-0 border-r border-hair bg-transparent px-4 py-3 font-sans text-[13px] whitespace-nowrap text-fg transition-colors duration-200 hover:bg-hover hover:text-accent"
                    >
                        Keuze bewaren
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={() => setSettings(true)}
                        className="flex-none cursor-pointer border-0 border-r border-hair bg-transparent px-4 py-3 font-mono text-[11px] whitespace-nowrap text-faint transition-colors duration-200 hover:bg-hover hover:text-fg"
                    >
                        Instellen
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => decide(NECESSARY_ONLY, 'necessary_only')}
                    className="flex-none cursor-pointer border-0 border-r border-hair bg-transparent px-4 py-3 font-sans text-[13px] whitespace-nowrap text-fg transition-colors duration-200 hover:bg-hover hover:text-accent"
                >
                    Alleen noodzakelijk
                </button>
                <button
                    type="button"
                    onClick={() => decide(ACCEPT_ALL, 'accept_all')}
                    className="flex-1 cursor-pointer border-0 bg-button px-4 py-3 font-sans text-[13px] font-medium whitespace-nowrap text-button-text transition-opacity duration-200 hover:opacity-80"
                >
                    Accepteren
                </button>
            </div>
        </div>
    )
}

/** A checkbox that reads as a line of the design rather than as a form. */
function Toggle({
    label,
    hint,
    checked,
    onChange,
}: {
    label: string
    hint: string
    checked: boolean
    onChange: (checked: boolean) => void
}) {
    return (
        <label className="flex cursor-pointer items-start gap-2.5">
            <input
                type="checkbox"
                checked={checked}
                onChange={(event) => onChange(event.target.checked)}
                className="mt-[3px] cursor-pointer accent-accent"
            />
            <span className="flex flex-col gap-[3px]">
                <span className="text-[13.5px] text-fg">{label}</span>
                <span className="font-mono text-[10.5px] leading-relaxed text-faint">{hint}</span>
            </span>
        </label>
    )
}
